import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';

import mainBanner from '@/assets/img/banner/membership.jpg';
import SubBanner from '@/components/common/Banner/SubBanner/SubBanner';
import CompleteModal from '@/components/common/Modal/CompleteModal/CompleteModal';
import ModalBox from '@/components/common/Modal/ModalBox/ModalBox';
import ModalPortal from '@/components/common/Modal/ModalPortal/ModalPortal';
import MembershipGuide from '@/components/Membership/MembershipGuide/MembershipGuide.tsx';
import MembershipPayment from '@/components/Membership/MembershipPayment/MembershipPayment.tsx';
import { useModalStore } from '@/store/modalStore';
import { useTokenStore } from '@/store/tokenStore';

import * as S from './style';

const MENU_GUIDE = '가입안내';
const MENU_PAYMENT = '연회비 납부';

const Membership = () => {
	const { t } = useTranslation();
	const location = useLocation();
	const activeMenu = location.state?.menu ?? MENU_GUIDE;

	const [searchParams] = useSearchParams();
	const successParams = searchParams.get('sucess') ?? '';
	const failParams = searchParams.get('aid') ?? '';

	const navigate = useNavigate();
	const { showModal } = useModalStore();
	const { isLogin } = useTokenStore();

	const [isOpen, setIsOpen] = useState(false);
	const guideRef = useRef<HTMLElement>(null);
	const paymentRef = useRef<HTMLElement>(null);
	const loginGuardShown = useRef(false);

	const isGuideMenu = activeMenu === MENU_GUIDE;
	const isPaymentMenu = activeMenu === MENU_PAYMENT;
	const requiresLogin = isGuideMenu || isPaymentMenu;
	const canViewPage = isLogin() || !requiresLogin;

	const promptLogin = () => {
		if (loginGuardShown.current) return;
		loginGuardShown.current = true;
		showModal(t('common.alerts.loginRequired'), 'login', t('common.actions.goToLogin'), () => navigate('/auth/login'));
		navigate('/', { replace: true });
	};

	useEffect(() => {
		if (!requiresLogin || isLogin()) return;
		promptLogin();
	}, [requiresLogin, isLogin]);

	useEffect(() => {
		if (!canViewPage || !location.state?.menu) return;
		const target = activeMenu === MENU_PAYMENT ? paymentRef.current : guideRef.current;
		target?.scrollIntoView({ behavior: 'smooth', block: 'start' });
	}, [activeMenu, canViewPage, location.state?.menu]);

	useEffect(() => {
		if (!isLogin()) return;

		const menuState = { menu: MENU_PAYMENT };

		if (successParams === 'true') {
			setIsOpen(true);
			navigate('/membership', { replace: true, state: menuState });
			return;
		}

		if (successParams === 'false' || failParams !== '') {
			showModal(t('common.alerts.paymentFailed'));
			navigate('/membership', { replace: true, state: menuState });
		}
	}, [successParams, failParams, isLogin, navigate, showModal, t]);

	if (!canViewPage) {
		return null;
	}

	return (
		<main>
			<SubBanner subTitle="membership.page.bannerSubtitle" title="membership.page.bannerTitle" img={mainBanner} />

			{isLogin() && (
				<S.Section id="membership-guide" ref={guideRef} aria-labelledby="membership-guide-title">
					<MembershipGuide />
				</S.Section>
			)}

			<S.Section id="membership-payment" ref={paymentRef} aria-labelledby="membership-payment-title">
				<MembershipPayment />
			</S.Section>

			{isOpen && (
				<ModalPortal>
					<ModalBox onClick={() => setIsOpen(false)}>
						<CompleteModal closeModal={setIsOpen} />
					</ModalBox>
				</ModalPortal>
			)}
		</main>
	);
};

export default Membership;
